import * as React from 'react'

import { parseUluleStats, ULULE_STATS_URL, type UluleStats } from './ululeStats'

/**
 * Platform stats for a prerendered page: start from the build-time snapshot embedded in the HTML,
 * then refresh once on mount so a long-cached page still shows current numbers.
 *
 * Returns `null` when prerendering could not reach `stats.json` AND the client fetch also failed —
 * callers render no figures in that case rather than zeros.
 */
export function useUluleStats(initialStats: UluleStats | null): UluleStats | null {
  const [stats, setStats] = React.useState(initialStats)

  React.useEffect(() => {
    const controller = new AbortController()

    async function refreshStats(): Promise<void> {
      try {
        const response = await fetch(ULULE_STATS_URL, { signal: controller.signal })
        if (!response.ok) return

        const updatedAt = response.headers.get('Last-Modified') ?? new Date().toISOString()
        const nextStats = parseUluleStats(await response.json(), updatedAt)
        if (nextStats !== null) setStats(nextStats)
      } catch {
        // Keep the build-time snapshot, or no figures when prerendering also failed.
      }
    }

    void refreshStats()
    return () => controller.abort()
  }, [])

  return stats
}
